import React, {useMemo, useState} from 'react';
import {
	AssignedRequirement,
	AssignedRequirementFilterOptions,
	AssignedRequirementRef,
	Requirement,
	RequirementFilterOptions,
	VehicleProject,
} from 'types';
import {
	funcDecodeHex24ToDocId,
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList';
import {renderArrayField} from 'components/EntityList/ColumnRenderers';
import {useTranslation} from 'react-i18next';
import {
	useRequirementColumnMetaData,
	useRequirementsPageColumn,
	useRequirementsPageFilterOptions,
} from 'features/Requirements/RequirementsPageColumns';
import {
	queryStringMongoArraySTD,
	queryStringMongoEnumSTD,
} from 'components/InfinityList/InfinitListStandardHooks';
import {getProjectName} from '../VehicleProjectDetails/vehicleProjectDetailsPage';
import {notProjectRelevantRowStyles} from '../entityListStyles';

export type AssignedRequirementWithID = Requirement &
	Partial<AssignedRequirementRef> & {
		vehicleProjects?: VehicleProject[];
		isProjectRelevant?: boolean;
		projectRelevantComments?: string;
	};

export const mapProjectRequirement = (assignedReq: AssignedRequirement) => {
	return {
		...assignedReq,
		...assignedReq.requirement,
	} as AssignedRequirementWithID;
};

export const resolveVehicleProjects = (
	assignedReq: AssignedRequirementWithID,
	dictVehicleProjects: Record<string, VehicleProject>,
) => {
	const vehicleProjects = (assignedReq.vehicleProjectsRefs ?? [])
		.map(y => dictVehicleProjects[funcDecodeHex24ToDocId(y.id)])
		.filter(x => x !== undefined);

	return {...assignedReq, vehicleProjects};
};

export const useAssignedRequirementsColumn = () => {
	const {t: tAssigned, i18n} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'AssignedRequirementsFields',
	});
	const {t: tRelevant} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'ProjectRelevantFields',
	});
	const columnsRequirements = useRequirementsPageColumn();

	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	return [
		{
			key: 'vehicleProjects',
			name: tAssigned('VehicleProjects'),
			fieldName: 'vehicleProjects',
			minWidth: 150,
			maxWidth: 200,
			isResizable: true,
			onRender: renderArrayField((item: VehicleProject) =>
				getProjectName(item, i18n),
			),
			...filterArraySimple,
		},
		{
			key: 'isProjectRelevant',
			name: tRelevant('ColumnProjectRelevant'),
			fieldName: 'isProjectRelevant',
			minWidth: 120,
			maxWidth: 150,
			isResizable: true,
			onRender: (item: AssignedRequirementWithID) =>
				item.isProjectRelevant ? tRelevant('Yes') : tRelevant('No'),
		},
		{
			key: 'projectRelevantComments',
			name: tRelevant('ColumnJustification'),
			fieldName: 'projectRelevantComments',
			minWidth: 200,
			maxWidth: 300,
			isResizable: true,
			onRender: (item: AssignedRequirementWithID) =>
				item.projectRelevantComments || '',
		},
		{
			key: 'addedAutomatically',
			name: tAssigned('AddedAutomatically'),
			fieldName: 'addedAutomatically',
			isResizable: true,
			minWidth: 100,
			maxWidth: 100,
			onRender: (item: AssignedRequirementWithID) =>
				item.addedAutomatically ? tAssigned('true') : tAssigned('false'),
			...filterArraySimple,
		},
		{
			key: 'addReason',
			name: tAssigned('AddReason'),
			fieldName: 'addReason',
			isResizable: true,
			minWidth: 150,
			maxWidth: 150,
			onRender: (item: AssignedRequirementWithID) =>
				item.addReason === 'AutomaticAssignment'
					? tAssigned('AutomaticAssignment')
					: item.addReason,
		},
		...columnsRequirements.slice(1),
	] as InfinityListColumn[];
};

export const useAssignedRequirementsPageFilterOptions = (
	dictVehicleProjects: Record<string, VehicleProject>,
	requirementFilterOptions?: RequirementFilterOptions,
	assignedRequirementFilterOptions?: AssignedRequirementFilterOptions,
) => {
	const {t, i18n} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'AssignedRequirementsFields',
	});

	const columnFilterOptionsRequirments = useRequirementsPageFilterOptions(
		requirementFilterOptions,
	);

	const queryData = assignedRequirementFilterOptions;

	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(() => {
		const filterOptions = [
			{
				key: 'vehicleProjects',
				fieldValuesUnique: (queryData?.vehicleProjectOptions ?? []).map(x => ({
					...x,
					name: getProjectName(dictVehicleProjects[x.optionKey], i18n),
				})),
			},
			{
				key: 'addedAutomatically',
				fieldValuesUnique: (queryData?.addedAutomaticallyOptions ?? []).map(
					x => ({optionKey: x, name: t(x.toLocaleLowerCase())}),
				),
			},
		];
		return filterOptions;
	}, [assignedRequirementFilterOptions, dictVehicleProjects]);
	return [...columnFilterOptions, ...columnFilterOptionsRequirments];
};

export const useAssignedRequirementColumnMetaData = () => {
	const columnMetaDataRequirments = useRequirementColumnMetaData();
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'vehicleProjects',
			mongoDBFn: queryStringMongoArraySTD('vehicleProjectsRefs._id'),
		},
		{
			key: 'addedAutomatically',
			mongoDBFn: queryStringMongoEnumSTD('addedAutomatically'),
		},
		...columnMetaDataRequirments,
	];
	return columnMetaData;
};

export const assignedReqFields = [
	'vehicleProjects',
	'isProjectRelevant',
	'projectRelevantComments',
	'addedAutomatically',
	'addReason',
];

export const useAssignedRequirementRowStyles = () => {
	return React.useCallback((item: AssignedRequirementWithID) => {
		if (item.isProjectRelevant === false) {
			return notProjectRelevantRowStyles;
		}
		return undefined;
	}, []);
};
